import { Link } from "@tanstack/react-router";
import { ArrowUpDown, ExternalLink, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  type DrillDownDeal,
  formatCurrency,
  formatDate,
  formatNumber,
  getPipelineName,
  getStageName,
  type Pipeline,
  resolvePipelineIds,
} from "@/components/deal-metrics/shared";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ExecutiveDashboardResponse,
  type ExecutiveMetricKey,
  formatLongMonthLabel,
} from "./types";
import { YoyDelta } from "./yoy-delta";

type DrillMetric = "opportunities" | "closedDeals" | "grossSales" | "netSales";

interface ExecutiveDrillDownPageProps {
  metric: DrillMetric;
  month: string;
  pipelines: Pipeline[];
}

const EXEC_KEY_MAP: Record<DrillMetric, ExecutiveMetricKey> = {
  opportunities: "opportunities",
  closedDeals: "orders",
  grossSales: "grossSales",
  netSales: "netSales",
};

function priorYearMonth(month: string): string {
  const [yStr, mStr] = month.split("-");
  const y = Number(yStr) - 1;
  return `${y}-${mStr}`;
}

const METRIC_META: Record<
  DrillMetric,
  { label: string; format: "count" | "currency"; formula: string; amountField: keyof DrillDownDeal }
> = {
  opportunities: {
    label: "Opportunities",
    format: "count",
    formula:
      "Count of deals with a Free Quote Form Filled date in this month, excluding Closed Lost and Returned/Refunded stages.",
    amountField: "amount_in_home_currency",
  },
  closedDeals: {
    label: "Orders",
    format: "count",
    formula:
      "Count of deals with an Order Date in this month in the Production pipeline, excluding Returned/Refunded stages.",
    amountField: "amount_in_home_currency",
  },
  grossSales: {
    label: "Gross Sales",
    format: "currency",
    formula:
      "Sum of Amount (home currency) for Production-pipeline deals ordered in this month, excluding Returned/Refunded stages.",
    amountField: "amount_in_home_currency",
  },
  netSales: {
    label: "Net Sales",
    format: "currency",
    formula:
      "Sum of Net Price for Production-pipeline deals ordered in this month, excluding Returned/Refunded stages.",
    amountField: "net_price",
  },
};

type SortKey = "dealname" | "amount" | "date" | "stage" | "pipeline";
type SortDir = "asc" | "desc";

export function ExecutiveDrillDownPage({ metric, month, pipelines }: ExecutiveDrillDownPageProps) {
  const [deals, setDeals] = useState<DrillDownDeal[]>([]);
  const [execData, setExecData] = useState<ExecutiveDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: "date", dir: "desc" });

  const meta = METRIC_META[metric];
  const isOpp = metric === "opportunities";
  const monthLabel = formatLongMonthLabel(month);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { prodPipelineId, salesPipelineId, excludeOppStageIds, excludeClosedStageIds } =
          resolvePipelineIds(pipelines);
        const params = new URLSearchParams();
        params.set("metric", metric);
        params.set("section", "main");
        params.set("month", month);
        params.set("companyWide", "true");
        if (prodPipelineId) params.set("prodPipelineId", prodPipelineId);
        if (salesPipelineId) params.set("salesPipelineId", salesPipelineId);
        params.set("excludeOppStageIds", excludeOppStageIds.join(","));
        params.set("excludeClosedStageIds", excludeClosedStageIds.join(","));
        params.set("startDate", "1970-01-01");
        params.set("endDate", "9999-12-31");
        const [dealsRes, execRes] = await Promise.all([
          fetch(`/api/owner-metrics/_/drill-down?${params}`),
          fetch("/api/executive-dashboard"),
        ]);
        if (!dealsRes.ok) {
          const text = await dealsRes.text();
          throw new Error(text || `HTTP ${dealsRes.status}`);
        }
        const dealsJson = await dealsRes.json();
        if (!cancelled) setDeals((dealsJson.deals ?? []) as DrillDownDeal[]);
        if (execRes.ok) {
          const execJson = (await execRes.json()) as ExecutiveDashboardResponse;
          if (!cancelled) setExecData(execJson);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load deals");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [metric, month, pipelines]);

  const totalValue = useMemo(() => {
    if (meta.format === "count") return deals.length;
    return deals.reduce((sum, d) => sum + (Number(d[meta.amountField]) || 0), 0);
  }, [deals, meta.format, meta.amountField]);

  const yoyContext = useMemo(() => {
    if (!execData) return null;
    const execKey = EXEC_KEY_MAP[metric];
    const priorMonth = priorYearMonth(month);
    const isProrated = execData.currentMonthYoy?.month === month;
    const priorRow = isProrated
      ? execData.currentMonthYoy?.priorRow
      : execData.rows.find((r) => r.month === priorMonth);
    const priorValue = priorRow ? priorRow[execKey] : null;
    const delta = execData.yoyByMonth[month]?.[execKey] ?? null;
    const asOfDay = isProrated ? (execData.currentMonthYoy?.asOfDayOfMonth ?? null) : null;
    return { priorMonth, priorValue, delta, asOfDay };
  }, [execData, metric, month]);

  const secondaryStat = useMemo(() => {
    if (metric === "grossSales") {
      const net = deals.reduce((s, d) => s + (Number(d.net_price) || 0), 0);
      return { label: "Net Sales", value: formatCurrency(net) };
    }
    if (metric === "netSales") {
      const gross = deals.reduce((s, d) => s + (Number(d.amount_in_home_currency) || 0), 0);
      return { label: "Gross Sales", value: formatCurrency(gross) };
    }
    if (metric === "closedDeals") {
      const gross = deals.reduce((s, d) => s + (Number(d.amount_in_home_currency) || 0), 0);
      return { label: "Gross Sales", value: formatCurrency(gross) };
    }
    return null;
  }, [deals, metric]);

  const filteredAndSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = term
      ? deals.filter((d) => d.dealname?.toLowerCase().includes(term))
      : deals.slice();
    base.sort((a, b) => {
      const dir = sort.dir === "asc" ? 1 : -1;
      let av: string | number | null = null;
      let bv: string | number | null = null;
      switch (sort.key) {
        case "dealname":
          av = a.dealname ?? "";
          bv = b.dealname ?? "";
          break;
        case "amount":
          av = Number(a[meta.amountField]) || 0;
          bv = Number(b[meta.amountField]) || 0;
          break;
        case "date":
          av = (isOpp ? a.free_quote_form_filled : a.order_date) ?? "";
          bv = (isOpp ? b.free_quote_form_filled : b.order_date) ?? "";
          break;
        case "stage":
          av = getStageName(a.dealstage, pipelines);
          bv = getStageName(b.dealstage, pipelines);
          break;
        case "pipeline":
          av = getPipelineName(a.pipeline, pipelines);
          bv = getPipelineName(b.pipeline, pipelines);
          break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return base;
  }, [deals, search, sort, meta.amountField, isOpp, pipelines]);

  const toggleSort = (key: SortKey) => {
    setSort((cur) =>
      cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" },
    );
  };

  return (
    <div className="space-y-6 p-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/executive">Executive</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              {meta.label} · {monthLabel}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card>
        <CardContent className="p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            {monthLabel}
          </div>
          <h1 className="font-serif text-2xl text-foreground mt-1">{meta.label}</h1>
          <div className="mt-3 flex items-baseline gap-4 flex-wrap">
            <div className="font-serif text-4xl text-foreground tabular-nums">
              {loading
                ? "—"
                : meta.format === "currency"
                  ? formatCurrency(totalValue)
                  : formatNumber(totalValue)}
            </div>
            {yoyContext && <YoyDelta delta={yoyContext.delta} size="lg" />}
          </div>
          {yoyContext && (
            <div className="mt-2 text-sm text-muted-foreground">
              vs{" "}
              <span className="font-semibold text-foreground tabular-nums">
                {yoyContext.priorValue === null
                  ? "—"
                  : meta.format === "currency"
                    ? formatCurrency(yoyContext.priorValue)
                    : formatNumber(Math.round(yoyContext.priorValue))}
              </span>{" "}
              in {formatLongMonthLabel(yoyContext.priorMonth)}
              {yoyContext.asOfDay ? ` (1–${yoyContext.asOfDay}, same window)` : ""}
            </div>
          )}
          <p className="mt-4 text-sm text-muted-foreground max-w-2xl">{meta.formula}</p>
          {secondaryStat && (
            <div className="mt-4 text-sm">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                {secondaryStat.label}
              </span>{" "}
              <span className="font-semibold tabular-nums">
                {loading ? "—" : secondaryStat.value}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <div className="text-sm text-muted-foreground tabular-nums">
          {loading ? "Loading…" : `${formatNumber(filteredAndSorted.length)} deals`}
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <SortableHead
                    onClick={() => toggleSort("dealname")}
                    active={sort.key === "dealname"}
                    dir={sort.dir}
                  >
                    Deal Name
                  </SortableHead>
                  <SortableHead
                    onClick={() => toggleSort("amount")}
                    active={sort.key === "amount"}
                    dir={sort.dir}
                    align="right"
                  >
                    Amount
                  </SortableHead>
                  <SortableHead
                    onClick={() => toggleSort("date")}
                    active={sort.key === "date"}
                    dir={sort.dir}
                  >
                    Date
                  </SortableHead>
                  <SortableHead
                    onClick={() => toggleSort("stage")}
                    active={sort.key === "stage"}
                    dir={sort.dir}
                  >
                    Stage
                  </SortableHead>
                  <SortableHead
                    onClick={() => toggleSort("pipeline")}
                    active={sort.key === "pipeline"}
                    dir={sort.dir}
                  >
                    Pipeline
                  </SortableHead>
                  <TableHead className="w-[60px]">HS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      <TableCell colSpan={6}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredAndSorted.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-sm text-muted-foreground py-12"
                    >
                      {search ? "No deals match your search." : "No deals found for this month."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSorted.map((d) => (
                    <TableRow key={d.hubspot_deal_id}>
                      <TableCell className="font-medium max-w-[260px] truncate">
                        {d.dealname ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(Number(d[meta.amountField]) || null)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {isOpp ? formatDate(d.free_quote_form_filled) : formatDate(d.order_date)}
                      </TableCell>
                      <TableCell>{getStageName(d.dealstage, pipelines)}</TableCell>
                      <TableCell>{getPipelineName(d.pipeline, pipelines)}</TableCell>
                      <TableCell>
                        <a
                          href={`https://app.hubspot.com/contacts/${d.hubspot_deal_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-brand-navy hover:underline"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

function SortableHead({
  children,
  onClick,
  active,
  dir,
  align,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: SortDir;
  align?: "right";
}) {
  return (
    <TableHead className={align === "right" ? "text-right" : undefined}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-foreground ${active ? "text-foreground" : ""}`}
      >
        {children}
        <ArrowUpDown className={`h-3 w-3 ${active ? "opacity-100" : "opacity-40"}`} />
        {active && <span className="sr-only">{dir === "asc" ? "ascending" : "descending"}</span>}
      </button>
    </TableHead>
  );
}
